import { openDB, type IDBPDatabase } from 'idb';
import type { IXpProfile, IXpEvent, XpDB, XpAwardResult } from './xp-types';
import { XP_VALUES, getLevelForXp } from './xp-constants';

const DB_NAME = 'referee_xp';
const DB_VERSION = 1;
const MAX_EVENTS = 1000;
const MAX_PRACTICE_DATES = 400;

function getToday(): string {
  return new Date().toISOString().slice(0, 10);
}

function createEmptyProfile(): IXpProfile {
  return {
    totalXp: 0,
    practiceDates: [],
    currentDailyStreak: 0,
    bestDailyStreak: 0,
    lastPracticeDate: null,
    masteredCategories: [],
  };
}

export class XpManager {
  private db: IDBPDatabase<XpDB> | null = null;
  private profile: IXpProfile = createEmptyProfile();

  async initialize(): Promise<void> {
    try {
      this.db = await openDB<XpDB>(DB_NAME, DB_VERSION, {
        upgrade(db) {
          if (!db.objectStoreNames.contains('xpProfile')) {
            db.createObjectStore('xpProfile');
          }
          if (!db.objectStoreNames.contains('xpEvents')) {
            db.createObjectStore('xpEvents', { autoIncrement: true });
          }
        },
      });

      const stored = await this.db.get('xpProfile', 'profile');
      this.profile = stored ?? createEmptyProfile();
    } catch {
      this.db = null;
      this.profile = createEmptyProfile();
    }
  }

  getProfile(): IXpProfile {
    return { ...this.profile };
  }

  async getRecentEvents(limit = 50): Promise<IXpEvent[]> {
    if (!this.db) {
      return [];
    }

    const tx = this.db.transaction('xpEvents', 'readonly');
    const events: IXpEvent[] = [];
    let cursor = await tx.store.openCursor(null, 'prev');

    while (cursor && events.length < limit) {
      events.push(cursor.value);
      cursor = await cursor.continue();
    }

    return events;
  }

  processAnswer(
    correct: boolean,
    prevBox: number,
    newBox: number,
    rule: string,
    version: string,
    allRuleQuestions: { rule: string; isMastered: boolean }[],
  ): XpAwardResult {
    const events: IXpEvent[] = [];
    const now = new Date();
    const today = getToday();

    if (correct) {
      events.push({ type: 'correct_answer', xp: XP_VALUES.correctAnswer, timestamp: now });

      if (newBox > prevBox && XP_VALUES.boxPromotion[newBox]) {
        events.push({
          type: 'box_promotion',
          xp: XP_VALUES.boxPromotion[newBox],
          timestamp: now,
          metadata: { fromBox: prevBox, toBox: newBox },
        });
      }

      const categoryKey = `${version}:${rule}`;
      if (!this.profile.masteredCategories.includes(categoryKey)) {
        const ruleQuestions = allRuleQuestions.filter((q) => q.rule === rule);
        const allMastered = ruleQuestions.length > 0 && ruleQuestions.every((q) => q.isMastered);
        if (allMastered) {
          events.push({
            type: 'category_mastery',
            xp: XP_VALUES.categoryMastery,
            timestamp: now,
            metadata: { rule, version },
          });
        }
      }
    }

    // Daily streak — awarded on first answer of the day
    if (this.profile.lastPracticeDate !== today) {
      const streakInfo = this.calculateStreak(today);

      if (streakInfo.newStreak >= 2) {
        const streakXp = streakInfo.newStreak >= XP_VALUES.dailyStreakLongThreshold
          ? XP_VALUES.dailyStreakLong
          : XP_VALUES.dailyStreakShort;

        events.push({
          type: 'daily_streak',
          xp: streakXp,
          timestamp: now,
          metadata: { streak: streakInfo.newStreak },
        });
      }
    }

    const totalXpGained = events.reduce((sum, e) => sum + e.xp, 0);
    const previousLevel = getLevelForXp(this.profile.totalXp).level;
    const newTotalXp = this.profile.totalXp + totalXpGained;
    const newLevel = getLevelForXp(newTotalXp).level;

    return {
      totalXpGained,
      events,
      newTotalXp,
      previousLevel,
      newLevel,
      leveledUp: newLevel > previousLevel,
    };
  }

  async awardXp(result: XpAwardResult): Promise<void> {
    const today = getToday();

    // Update profile
    this.profile.totalXp = result.newTotalXp;

    if (this.profile.lastPracticeDate !== today) {
      const streakInfo = this.calculateStreak(today);
      this.profile.currentDailyStreak = streakInfo.newStreak;
      this.profile.bestDailyStreak = Math.max(this.profile.bestDailyStreak, streakInfo.newStreak);
      this.profile.lastPracticeDate = today;

      if (!this.profile.practiceDates.includes(today)) {
        this.profile.practiceDates.push(today);
        if (this.profile.practiceDates.length > MAX_PRACTICE_DATES) {
          this.profile.practiceDates = this.profile.practiceDates.slice(-MAX_PRACTICE_DATES);
        }
      }
    }

    // Track category mastery
    for (const event of result.events) {
      if (event.type === 'category_mastery' && event.metadata) {
        const key = `${event.metadata.version}:${event.metadata.rule}`;
        if (!this.profile.masteredCategories.includes(key)) {
          this.profile.masteredCategories.push(key);
        }
      }
    }

    if (!this.db) {
      return;
    }

    const tx = this.db.transaction(['xpProfile', 'xpEvents'], 'readwrite');
    await tx.objectStore('xpProfile').put({ ...this.profile }, 'profile');

    const eventStore = tx.objectStore('xpEvents');
    for (const event of result.events) {
      await eventStore.add(event);
    }

    await tx.done;

    // Cap events
    await this.capEvents();
  }

  async reset(): Promise<void> {
    this.profile = createEmptyProfile();

    if (!this.db) {
      return;
    }

    const tx = this.db.transaction(['xpProfile', 'xpEvents'], 'readwrite');
    await tx.objectStore('xpProfile').put({ ...this.profile }, 'profile');
    await tx.objectStore('xpEvents').clear();
    await tx.done;
  }

  private calculateStreak(today: string): { newStreak: number } {
    const lastDate = this.profile.lastPracticeDate;

    if (!lastDate) {
      return { newStreak: 1 };
    }

    const last = new Date(`${lastDate}T00:00:00`);
    const current = new Date(`${today}T00:00:00`);
    const diffMs = current.getTime() - last.getTime();
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 1) {
      return { newStreak: this.profile.currentDailyStreak + 1 };
    }

    if (diffDays === 0) {
      return { newStreak: this.profile.currentDailyStreak };
    }

    return { newStreak: 1 };
  }

  private async capEvents(): Promise<void> {
    if (!this.db) {
      return;
    }

    const tx = this.db.transaction('xpEvents', 'readwrite');
    const count = await tx.store.count();

    if (count > MAX_EVENTS) {
      const toDelete = count - MAX_EVENTS;
      let cursor = await tx.store.openCursor();
      let deleted = 0;

      while (cursor && deleted < toDelete) {
        await cursor.delete();
        deleted++;
        cursor = await cursor.continue();
      }
    }

    await tx.done;
  }
}
